"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Kbd } from "@/components/ui/kbd";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { loadStoredPermissionMode, nextDashboardPermissionMode, parseDashboardPermissionMode, storePermissionMode, PERMISSION_MODE_STORAGE_KEY, type DashboardPermissionMode } from "@/lib/dashboard-ops/permission-mode";

/**
 * Open/close state for the omnipresent dashboard assistant panel (AIR-663),
 * toggled from anywhere with ⌘⇧B (Ctrl+Shift+B on Windows/Linux) or the
 * floating button, plus the panel's action permission mode (ask/act), toggled
 * with Shift+Tab inside the panel. Mounted once in MainShell so the state
 * survives client-side navigation; the mode persists per browser.
 *
 * Note: Chrome binds ⌘⇧B to the bookmarks bar; preventDefault wins while the
 * page has focus, which is the case for any in-app keypress.
 */
const AssistantPanelContext = createContext<{
  open: boolean;
  /** Sticky: true once the panel has ever been opened this mount. Gates the
   * lazy pane mount without losing conversation state on later closes. */
  hasOpened: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  /** Action consent level for the assistant's writes (ask/act). */
  permissionMode: DashboardPermissionMode;
  cyclePermissionMode: () => void;
} | null>(null);
export function useAssistantPanel() {
  const context = useContext(AssistantPanelContext);
  if (!context) {
    throw new Error("useAssistantPanel must be used within AssistantPanelProvider");
  }
  return context;
}

/**
 * Same context, tolerating absence: the approval card renders through the
 * shared ChatMessage and may mount where no panel provider exists (/chat's
 * read-only history of a dashboard thread).
 */
export function useOptionalAssistantPanel() {
  return useContext(AssistantPanelContext);
}
export function AssistantPanelProvider(t0) {
  const $ = _c(17);
  const {
    children
  } = t0;
  const [open, setOpenState] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [permissionMode, setPermissionMode] = useState(loadStoredPermissionMode);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = next => {
      setOpenState(next);
      if (next) {
        setHasOpened(true);
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const setOpen = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setOpenState(_temp);
      setHasOpened(true);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const toggle = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setPermissionMode(nextDashboardPermissionMode);
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const cyclePermissionMode = t3;
  let t4;
  let t5;
  if ($[3] !== permissionMode) {
    t4 = () => {
      storePermissionMode(permissionMode);
    };
    t5 = [permissionMode];
    $[3] = permissionMode;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      const onKeyDown = event => {
        if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.code === "KeyB") {
          event.preventDefault();
          toggle();
        }
      };
      window.addEventListener("keydown", onKeyDown);
      return () => window.removeEventListener("keydown", onKeyDown);
    };
    t7 = [toggle];
    $[6] = t6;
    $[7] = t7;
  } else {
    t6 = $[6];
    t7 = $[7];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      const onStorage = event_0 => {
        if (event_0.key !== PERMISSION_MODE_STORAGE_KEY) {
          return;
        }
        setPermissionMode(parseDashboardPermissionMode(event_0.newValue));
      };
      window.addEventListener("storage", onStorage);
      return () => window.removeEventListener("storage", onStorage);
    };
    t9 = [];
    $[8] = t8;
    $[9] = t9;
  } else {
    t8 = $[8];
    t9 = $[9];
  }
  useEffect(t8, t9);
  let t10;
  if ($[10] !== hasOpened || $[11] !== open || $[12] !== permissionMode) {
    t10 = {
      open,
      hasOpened,
      setOpen,
      toggle,
      permissionMode,
      cyclePermissionMode
    };
    $[10] = hasOpened;
    $[11] = open;
    $[12] = permissionMode;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const value = t10;
  let t11;
  if ($[14] !== children || $[15] !== value) {
    t11 = <AssistantPanelContext.Provider value={value}>{children}</AssistantPanelContext.Provider>;
    $[14] = children;
    $[15] = value;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  return t11;
}

/** Floating affordance for the assistant while the panel is closed. */
function _temp(current) {
  return !current;
}
export function AssistantPanelFab() {
  const $ = _c(6);
  const {
    open,
    toggle
  } = useAssistantPanel();
  if (open) {
    return null;
  }
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Sparkles className="size-4" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== toggle) {
    t1 = <TooltipTrigger asChild={true}><Button size="icon" variant="outline" onClick={toggle} aria-label="Open assistant" className="fixed bottom-5 right-5 z-40 size-10 rounded-full shadow-md">{t0}</Button></TooltipTrigger>;
    $[1] = toggle;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <TooltipContent side="left"><span className="flex items-center gap-1.5">Assistant <Kbd>⌘⇧B</Kbd></span></TooltipContent>;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1) {
    t3 = <TooltipProvider delayDuration={0}><Tooltip>{t1}{t2}</Tooltip></TooltipProvider>;
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}